import React, { useState, useEffect, useCallback } from 'react'
import {
  Avatar,
  Button,
  Icon,
  ListItemIcon,
  ListItemText,
  Popover,
  MenuItem,
  Typography,
  Divider,
  Tooltip,
} from '@material-ui/core'
import { useSelector, useDispatch } from 'react-redux'
import * as authActions from 'app/auth/store/actions'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/styles'
import NoticeRestrictedPlace from 'app/components/NoticeRestrictedPlace'
import ChangePasswordDialog from 'app/main/changePassword/ChangePasswordDialog'
import ChangePasswordDialogFormik from 'app/main/changePassword/ChangePasswordDialogFormik'
import SetPasswordDialogFormik from 'app/main/changePassword/SetPasswordDialogFormik'

import {
  changePassword,
  setPassword,
} from 'app/main/changePassword/ChangePasswordRemote'
import useSubmitWithDialog from 'commons/useSubmitWithDialog'
import PersonFormDialog from 'app/components/PersonFormDialog'
import { useUserProfile } from 'commons/useUserProfile'
import * as R from 'ramda'
import {
  submitAdminProfileEffect,
  useOnUpdateAdminProfileSuccess,
} from 'app/main/userProfile/UpdateLocalAdminProfile'
import { useResultDispatch } from 'commons/useResultDispatch'
import { truncate } from 'libs/text'
import { errorTypes } from 'commons/error'
import { useBreakpoints } from 'commons/useBreakpoints'
import LanguageMenuSwitcher from './LanguageMenuSwitcher'
import HelpMenuSwitcher from './HelpMenuSwitcher'
import ConversationMenu from './ConversationMenu'
import { useAccessToken } from 'app/auth/authHooks'
import { setUnreadMessage } from 'app/store/actions/conversation.actions'
import endpointConfig from 'app/effects/apis/endpointConfig'
import { SocketEvent } from 'app/main/messages/conversations/socket'
import Assets from 'app/configs/assetConfigs'
import PersonAvatar from 'app/components/PersonAvatar'
import { makeConnection } from 'app/main/messages/conversations/socket'
import {
  openNewTab,
  privacyPolicyUrl,
  termsOfUseUrl,
  cookiePolicyUrl,
} from 'commons/website'
import useFeatureFlag from 'app/configs/useFeatureFlag'
import MessageAlert from 'app/components/MessageAlert'
import { useFlag } from '@unleash/proxy-client-react'

const useStyle = makeStyles((theme) => ({
  text: {
    color: '#3A3A3A !important',
  },
  icon: {
    color: '#3A3A3A !important',
  },
  customWidth: {
    width: '200px',
    borderRadius: 0,
  },
  avatar: {
    backgroundColor: `#${Math.floor(Math.random() * 16777215).toString(16)}`,
  },
  faqIcon: {
    color: '#3A3A3A !important',
    marginRight: '1rem',
    marginTop: '1rem',
    fontSize: '2.4rem',
  },
  fullWidthPopover: {
    '& .MuiListItem-root': {
      '& span': {
        color: theme.palette.darkGrey.main,
      },
    },
    [useBreakpoints(theme).lessThan.sm]: {
      width: '100%',
      maxWidth: 'unset',
      left: '0px !important',
      '& .MuiMenuItem-root': {
        fontSize: '3.6rem !important',
        '& .MuiListItemIcon-root': {
          '& span': {
            fontSize: '3.6rem !important',
            verticalAlign: 'middle',
            marginRight: '1.8rem',
            marginLeft: '1.6rem',
          },
        },
        '& .MuiListItemText-root': {
          '& span': {
            fontSize: '2.0rem !important',
            verticalAlign: 'middle',
          },
        },
      },
    },
  },
}))

const truncateUserRoleTitle = truncate(20)

const hasUnreadMessage = R.o(R.lt(0), R.prop('unread_messages_count'))

function UserMenu(props) {
  const classes = useStyle(props)
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const user = useSelector(({ auth }) => auth.user)

  const { mes_4164_profile_password } = useFeatureFlag()
  const mes_5122 = useFlag('mes_5122_navigation_support_manager_web')

  const { dispatchError, dispatchSuccess } = useResultDispatch()

  const [userMenu, setUserMenu] = useState(null)
  const [socket, setSocket] = useState(null)
  const userMenuClick = (event) => {
    setUserMenu(event.currentTarget)
  }

  const userMenuClose = () => {
    setUserMenu(null)
  }

  const unreadMessageListener = useCallback(
    (msgData) => {
      dispatch(setUnreadMessage(hasUnreadMessage(msgData)))
    },
    [socket], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const accessToken = useAccessToken()

  useEffect(() => {
    if (!accessToken || !R.isNil(socket)) return
    const socketInstance = makeConnection(
      endpointConfig.socketUrl(),
      accessToken,
    )
    socketInstance.on(SocketEvent.ADMIN_COUNTER, unreadMessageListener)
    setSocket(socketInstance)
  }, [accessToken, unreadMessageListener]) // eslint-disable-line react-hooks/exhaustive-deps

  const { formatMessage: f } = useIntl()
  const onUpdateAdminProfileSuccess = useOnUpdateAdminProfileSuccess()
  const {
    openDialog: openPwdDialog,
    closeDialog: closePwdDialog,
    submit,
    submitSetPassword,
    state: changePwdState,
  } = useSubmitWithDialog({
    submitEffect: changePassword,
    submitEffectSetPassword: setPassword,
    onSubmitSuccess: () => {
      onUpdateAdminProfileSuccess()
      dispatchSuccess(f({ id: 'adminPassword.result.success' }))
    },
    onError: (error) => {
      if (error?.response?.data?.error?.name === 'PasswordMismatch') {
        dispatch(
          MessageAlert.showError({
            message: formatMessage({ id: 'changePassword.error.401' }),
          }),
        )
      } else {
        dispatchError(error, errorTypes.changePassword)
      }
    },
  })

  const onOpenChangePwd = () => {
    setUserMenu(null)
    openPwdDialog()
  }

  // User Profile

  const {
    openDialog: openUserProfile,
    closeDialog: closeUserProfile,
    submit: submitUserProfile,
    state: userProfilesState,
    deleted: deleteAdmin,
  } = useSubmitWithDialog({
    submitEffect: submitAdminProfileEffect,
    onSubmitSuccess: onUpdateAdminProfileSuccess,
    onError: dispatchError,
  })
  const onClickUserProfile = () => {
    setUserMenu(null)
    openUserProfile()
  }
  const currentUserProfile = useUserProfile()
  const userRoleTitle = R.compose(
    R.join(', '),
    R.sort(R.subtract),
    R.map(R.prop('name')),
    R.pathOr([], ['selectedPlace', 'roles']),
  )(user)

  return (
    <React.Fragment>
      <LanguageMenuSwitcher />
      {mes_5122 ? null : <ConversationMenu />}
      <HelpMenuSwitcher />

      <Popover
        open={Boolean(userMenu)}
        anchorEl={userMenu}
        onClose={userMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{ className: clsx(classes.fullWidthPopover, 'py-8') }}
      >
        <>
          <MenuItem onClick={onClickUserProfile}>
            <ListItemIcon className="min-w-40">
              <Icon>account_circle</Icon>
            </ListItemIcon>
            <ListItemText
              className="ltr:pl-0 rtl:pr-0"
              primary={f({ id: 'userMenu.userProfile.label' })}
            />
          </MenuItem>
          {user?.data?.hasPassword ? (
            <MenuItem onClick={onOpenChangePwd}>
              <ListItemIcon className="min-w-40">
                <svg
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.6209 3.5C8.65086 3.5 4.62086 7.53 4.62086 12.5C4.62086 12.56 4.63086 12.62 4.63086 12.69L2.79086 10.85L1.38086 12.26L5.62086 16.5L9.86086 12.26L8.45086 10.85L6.63086 12.67C6.63086 12.61 6.62086 12.56 6.62086 12.5C6.62086 8.64 9.76086 5.5 13.6209 5.5C17.4809 5.5 20.6209 8.64 20.6209 12.5C20.6209 16.36 17.4809 19.5 13.6209 19.5C11.7209 19.5 10.0009 18.74 8.74086 17.51L7.32086 18.92C8.94086 20.51 11.1709 21.5 13.6209 21.5C18.5909 21.5 22.6209 17.47 22.6209 12.5C22.6209 7.53 18.5909 3.5 13.6209 3.5ZM15.6209 11.5V10.5C15.6209 9.4 14.7209 8.5 13.6209 8.5C12.5209 8.5 11.6209 9.4 11.6209 10.5V11.5C11.0709 11.5 10.6209 11.95 10.6209 12.5V15.5C10.6209 16.05 11.0709 16.5 11.6209 16.5H15.6209C16.1709 16.5 16.6209 16.05 16.6209 15.5V12.5C16.6209 11.95 16.1709 11.5 15.6209 11.5ZM14.6209 11.5H12.6209V10.5C12.6209 9.95 13.0709 9.5 13.6209 9.5C14.1709 9.5 14.6209 9.95 14.6209 10.5V11.5Z"
                    fill="#183560"
                  />
                </svg>
              </ListItemIcon>

              <ListItemText
                className="ltr:pl-0 rtl:pr-0"
                primary={f({ id: 'userMenu.changePassword.label' })}
              />
            </MenuItem>
          ) : (
            <MenuItem onClick={onOpenChangePwd}>
              <ListItemIcon className="min-w-40">
                <Icon>lock_outline</Icon>
              </ListItemIcon>

              <ListItemText
                className="ltr:pl-0 rtl:pr-0"
                primary={f({ id: 'userMenu.setupPassword.label' })}
              />
            </MenuItem>
          )}
          <MenuItem onClick={() => openNewTab(termsOfUseUrl)}>
            <ListItemIcon className="min-w-40">
              <img src={Assets.termIcon} alt="Terms of use Icon" />
            </ListItemIcon>
            <ListItemText
              className="ltr:pl-0 rtl:pr-0"
              primary={f({ id: 'termOfService.title' })}
            />
          </MenuItem>

          <MenuItem onClick={() => openNewTab(privacyPolicyUrl)}>
            <ListItemIcon className="min-w-40">
              <img src={Assets.privacyIcon} alt="Privacy Icon" />
            </ListItemIcon>
            <ListItemText
              className="ltr:pl-0 rtl:pr-0"
              primary={f({ id: 'privacyPolicy.title' })}
            />
          </MenuItem>

          <MenuItem onClick={() => openNewTab(cookiePolicyUrl)}>
            <ListItemIcon className="min-w-40">
              <img src={Assets.cookieIcon} alt="Cookie Icon" />
            </ListItemIcon>
            <ListItemText
              className="ltr:pl-0 rtl:pr-0"
              primary={f({ id: 'cookiePolicy.title' })}
            />
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              dispatch(authActions.logoutUser())
              userMenuClose()
            }}
          >
            <ListItemIcon className="min-w-40">
              <Icon>exit_to_app</Icon>
            </ListItemIcon>
            <ListItemText
              className="ltr:pl-0 rtl:pr-0"
              primary={f({ id: 'userMenu.signOut.label' })}
            />
          </MenuItem>
        </>
      </Popover>
      <Button
        className="h-64 ltr:mr-4 rtl:ml-4"
        onClick={userMenuClick}
        data-test-id="user-menu"
      >
        {user.data.photoURL &&
        user.data.photoURL !== Assets.personSilhouetteImage ? (
          <Avatar
            className={classes.avatar}
            alt="user photo"
            src={user.data.photoURL}
          />
        ) : (
          <PersonAvatar
            placeHolder={R.path(['data', 'avatarCharacters'], user)}
            subId={user.data.id}
            alt={user.data.firstName}
          />
        )}

        <div className="hidden md:flex flex-col ltr:ml-12 rtl:mr-12 text-center">
          <Typography
            component="span"
            className={clsx(classes.text, 'normal-case font-600 flex')}
          >
            {user.data.displayName}
          </Typography>
          <Tooltip
            title={userRoleTitle}
            aria-label="add"
            classes={{ tooltip: classes.customWidth }}
          >
            <Typography className={clsx(classes.text, 'text-11 capitalize')}>
              {truncateUserRoleTitle(userRoleTitle)}
            </Typography>
          </Tooltip>
        </div>

        <Icon
          className={clsx(
            classes.icon,
            'text-16 ltr:ml-12 rtl:mr-12 hidden sm:flex',
          )}
          variant="action"
        >
          keyboard_arrow_down
        </Icon>
      </Button>
      <PersonFormDialog
        isOpen={userProfilesState.isOpenDialog}
        initialData={currentUserProfile}
        onClose={closeUserProfile}
        onSubmit={submitUserProfile}
        isPending={userProfilesState.isLoading}
        onDeleteAdmin={deleteAdmin}
      />
      <NoticeRestrictedPlace />
      {mes_4164_profile_password ? (
        user.data.hasPassword ? (
          <ChangePasswordDialogFormik
            isOpen={changePwdState.isOpenDialog}
            onCancel={closePwdDialog}
            onValidSubmit={submit}
            isPending={changePwdState.isLoading}
          />
        ) : (
          <SetPasswordDialogFormik
            isOpen={changePwdState.isOpenDialog}
            onCancel={closePwdDialog}
            onValidSubmit={submitSetPassword}
            isPending={changePwdState.isLoading}
          />
        )
      ) : (
        <ChangePasswordDialog
          isOpen={changePwdState.isOpenDialog}
          onCancel={closePwdDialog}
          onValidSubmit={submit}
          isPending={changePwdState.isLoading}
        />
      )}
    </React.Fragment>
  )
}

export default UserMenu
